import type { OrganizationMember } from '@/types';
import i18n from '@/i18n/config';

import React from 'react';
import { RiUserLine, RiShieldUserLine, RiDeleteBinLine, RiAddLine } from '@remixicon/react';
import {
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Skeleton,
  Button,
  Tooltip,
  useDisclosure
} from '@heroui/react';

import { UserCell } from '@/components/shared/UserCell';
import { RoleChip } from '@/components/shared/RoleChip';
import { ConfirmDeleteModal } from '@/components/shared/ConfirmDeleteModal';
import { AddMemberForm } from './AddMemberForm';
import { apiClient } from '@/utils/api';
import { useAtom } from 'jotai';
import { removeOrganizationMember } from '@/store';

interface MembersModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  members: OrganizationMember[];
  isLoading: boolean;
  organizationName?: string;
  organizationId?: number;
  onMembersChange: () => void;
}

export function MembersModal({
  isOpen,
  onOpenChange,
  members,
  isLoading,
  organizationName,
  organizationId,
  onMembersChange
}: MembersModalProps) {
  const columns = [
    { name: i18n.t('columns.user'), uid: 'user' },
    { name: i18n.t('filters.role'), uid: 'role' },
    { name: i18n.t('copy.job_title'), uid: 'job_title' },
    { name: i18n.t('filters.status'), uid: 'status' },
    { name: i18n.t('common.actions'), uid: 'actions', align: 'end' }
  ];

  // Add member modal state
  const { isOpen: isAddMemberOpen, onOpen: onAddMemberOpen, onOpenChange: onAddMemberOpenChange } = useDisclosure();

  // Delete confirmation modal state
  const {
    isOpen: isDeleteConfirmOpen,
    onOpen: onDeleteConfirmOpen,
    onOpenChange: onDeleteConfirmOpenChange
  } = useDisclosure();
  const [memberToDelete, setMemberToDelete] = React.useState<OrganizationMember | null>(null);
  const [isRemoving, setIsRemoving] = React.useState(false);
  const [, removeOrganizationMemberAtom] = useAtom(removeOrganizationMember);

  const handleDeleteClick = React.useCallback(
    (member: OrganizationMember) => {
      setMemberToDelete(member);
      onDeleteConfirmOpen();
    },
    [onDeleteConfirmOpen]
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!organizationId || !memberToDelete) return;

    try {
      setIsRemoving(true);
      await removeOrganizationMemberAtom({
        organizationId,
        memberId: memberToDelete.user_id
      });
      onMembersChange(); // Refresh members list
      onDeleteConfirmOpenChange(); // fixed: no arguments
      setMemberToDelete(null);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsRemoving(false);
    }
  }, [organizationId, memberToDelete, onMembersChange, onDeleteConfirmOpenChange, removeOrganizationMemberAtom]);

  const renderCell = React.useCallback(
    (member: OrganizationMember, columnKey: React.Key) => {
      switch (columnKey) {
        case 'user':
          return <UserCell user={member} />;
        case 'role':
          return <RoleChip role={member.role as any} />;
        case 'job_title':
          return member.job_title || '-';
        case 'status':
          return (
            <Chip color={member.status === 'accepted' ? 'success' : 'default'} size="sm" variant="flat">
              {member.status}
            </Chip>
          );
        case 'actions':
          return (
            <div className="flex items-center justify-end gap-2">
              <Tooltip content={i18n.t('copy.remove_member')}>
                <Button isIconOnly color="danger" size="sm" variant="light" onPress={() => handleDeleteClick(member)}>
                  <RiDeleteBinLine className="h-4 w-4" />
                </Button>
              </Tooltip>
            </div>
          );
        default:
          return null;
      }
    },
    [handleDeleteClick]
  );

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex w-full items-center gap-4">
                <span>{organizationName ? `${organizationName}'s Members` : 'Workspace Members'}</span>
                <Button
                  color="primary"
                  size="sm"
                  variant="flat"
                  startContent={<RiAddLine className="h-4 w-4" />}
                  onPress={onAddMemberOpen}
                >
                  Add Member
                </Button>
              </div>
            </ModalHeader>
            <ModalBody>
              <div className="max-h-[400px] overflow-y-auto">
                <Table
                  aria-label={i18n.t('copy.members_table')}
                  removeWrapper
                  classNames={{
                    th: 'px-2 py-2',
                    td: 'px-2 py-2'
                  }}
                >
                  <TableHeader columns={columns}>
                    {(column) => <TableColumn key={column.uid}>{column.name}</TableColumn>}
                  </TableHeader>
                  <TableBody
                    emptyContent="No members found"
                    items={
                      isLoading && members.length === 0
                        ? (Array.from({ length: 3 }, (_, i) => ({ id: `skeleton-${i}` })) as any)
                        : members
                    }
                  >
                    {(item: any) => {
                      // Check if this is a skeleton item
                      if (typeof item.id === 'string' && item.id.startsWith('skeleton-')) {
                        return (
                          <TableRow key={item.id}>
                            {columns.map((column) => (
                              <TableCell key={column.uid}>
                                <Skeleton className="h-4 w-full rounded-lg" />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      }

                      // Regular member item
                      const member = item as OrganizationMember;
                      return (
                        <TableRow key={`member-${member.organization_user_id}`}>
                          {(columnKey) => <TableCell>{renderCell(member, columnKey)}</TableCell>}
                        </TableRow>
                      );
                    }}
                  </TableBody>
                </Table>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>

      {/* Add Member Form */}
      {organizationId && (
        <AddMemberForm
          isOpen={isAddMemberOpen}
          onOpenChange={onAddMemberOpenChange}
          organizationId={organizationId}
          onSuccess={onMembersChange}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteConfirmOpen}
        onOpenChange={onDeleteConfirmOpenChange}
        title={i18n.t('copy.remove_member_1')}
        message={`Are you sure you want to remove ${memberToDelete?.first_name || memberToDelete?.email || 'this member'} from the workspace? This action cannot be undone.`}
        confirmText={i18n.t('copy.remove')}
        cancelText="Cancel"
        isLoading={isRemoving}
        onConfirm={handleConfirmDelete}
      />
    </Modal>
  );
}
